import type { CourseEventPatternIndex, CourseEventState, CupState, DriverInput, HazardState, ItemBoxState, ItemKind, KartController, KartHorn, KartPattern, KartSticker, KartSurface, KartTopper, KartTrail, KartWeightClass, ProjectileState, RaceClass, RacePhase, RaceState, RaceVariantState } from "../game/simulation/types";
import { normalizeKartWeightClass } from "../game/kartBuild";
import { cloneCup } from "../game/championship";
import { normalizeCustomTrackDefinition, normalizeCustomTrackId, type CustomTrackDefinition, type CustomTrackId } from "../game/simulation/customTrackSchema";

// v6 introduced definition-before-snapshot custom tracks. v7 additionally
// carries open-cup identities/names beyond the active twenty-kart grid; an
// older client would silently truncate those standings during cloneCup().
// v8 adds static classic-* course IDs: v7 clients lack that geometry and
// could otherwise silently resolve a classic snapshot onto the default track.
// v9 adds the authoritative player-autopilot command/state contract.
export const PROTOCOL_VERSION = 9;
export const SERVER_TICK_RATE = 60;
export const SNAPSHOT_RATE = 20;

export type ClientRole = "player" | "overlay";

export interface KartSnapshot {
  id: string;
  /** Optional server-authored AI identity; never accepted from a client hello. */
  driverProfileId?: string;
  /**
   * Optional presentation identity. This is a canonical Twitch login supplied
   * only for an actual Twitch kart or a verified browser account's kart. It
   * is deliberately separate from `name`, which may be a display label.
   */
  avatarLogin?: string;
  name: string;
  color: number;
  accentColor: number;
  variant: number;
  livery: number;
  wheelStyle: number;
  driverColor: number;
  weightClass: KartWeightClass;
  pattern: KartPattern;
  topper: KartTopper;
  sticker: KartSticker;
  underglow: number;
  horn: KartHorn;
  skidColor: number;
  trail: KartTrail;
  raceNumber: string;
  kind: KartController;
  /** Optional for compatibility; true is authoritative only for human karts. */
  autopilot?: boolean;
  x: number;
  y: number;
  z: number;
  velocityX: number;
  velocityZ: number;
  yaw: number;
  speed: number;
  launchCharge: number;
  distance: number;
  lateral: number;
  driftCharge: number;
  driftDirection: -1 | 0 | 1;
  boostTime: number;
  /** Optional while pre-counterplay snapshots age out. */
  itemDodgeTime?: number;
  itemImmunityTime?: number;
  /** Optional while older clients age out; Beer Goggles phasing timer. */
  ghostTime?: number;
  hiccupTime?: number;
  draftCharge: number;
  draftBoostTime: number;
  draftCooldown: number;
  draftTargetId: string | null;
  draftBoostCount: number;
  heldItem: ItemKind | null;
  itemHitCount?: number;
  lastItemHit?: ItemKind | null;
  itemStealCount?: number;
  lastItemStolen?: ItemKind | null;
  itemGuardCount?: number;
  kegDodgeCount?: number;
  lastItemDodge?: ItemKind | null;
  shieldTime: number;
  spinTime: number;
  /** Optional while pre-recovery-assist snapshots age out. */
  spinRecoveryTime?: number;
  hornSeq: number;
  verticalVelocity: number;
  airborne: boolean;
  airTime: number;
  trickQueued: boolean;
  /** Optional classic source-index jump latch; absent in older snapshots. */
  classicJumpedSampleIndex?: number | null;
  classicSampleIndex?: number;
  classicNextCheckpoint?: number;
  classicGatesPassed?: number;
  classicProgress?: number;
  onShortcut: boolean;
  surface: KartSurface;
  offRoadTime: number;
  wrongWayTime: number;
  recoveryTime: number;
  recoveryCount: number;
  /** Remaining immunity after the kart has already been slowed by an event. */
  courseEventCooldown?: number;
  /** Per-kart race total; optional while older snapshots age out. */
  courseEventHitCount?: number;
  signatureZoneId: string | null;
  signatureZoneTime: number;
  signatureZoneTriggerCount: number;
  signatureZoneLastLap: number;
  safeDistance: number;
  lap: number;
  place: number;
  finishedAt: number | null;
}

export interface RaceSnapshot {
  trackId: string;
  /** Optional for one-release backwards compatibility; authoritative servers always send it. */
  raceVariant?: RaceVariantState;
  raceClass: RaceClass;
  phase: RacePhase;
  elapsed: number;
  countdown: number;
  totalLaps: number;
  trackLength: number;
  karts: KartSnapshot[];
  itemBoxes: ItemBoxState[];
  projectiles: ProjectileState[];
  hazards: HazardState[];
  /** Optional for one-release backwards compatibility; authoritative servers always send it. */
  courseEvent?: (Omit<CourseEventState, "patternIndex"> & { patternIndex?: CourseEventPatternIndex }) | null;
  cup?: CupState;
}

/** One immutable saved-track definition used by the current race. */
export interface CustomTrackDescriptor {
  runtimeId: CustomTrackId;
  key: CustomTrackId;
  revision: number;
  def: CustomTrackDefinition;
}

export type ClientMessage =
  | {
      type: "hello";
      protocol: number;
      role: ClientRole;
      name?: string;
      color?: number;
      accentColor?: number;
      variant?: number;
      livery?: number;
      wheelStyle?: number;
      driverColor?: number;
      weightClass?: KartWeightClass;
      pattern?: KartPattern;
      topper?: KartTopper;
      sticker?: KartSticker;
      underglow?: number;
      horn?: KartHorn;
      skidColor?: number;
      trail?: KartTrail;
      raceNumber?: string;
      raceClass?: RaceClass;
      sessionId?: string;
      reconnectToken?: string;
    }
  | { type: "autopilot"; enabled: boolean }
  | { type: "input"; seq: number; targetTick: number; input: DriverInput }
  | { type: "leave" }
  | { type: "ping"; sentAt: number };

export type ServerMessage =
  | ({ type: "custom-track-definition" } & CustomTrackDescriptor)
  | {
      type: "welcome";
      protocol: number;
      role: ClientRole;
      clientId: string;
      localKartId: string | null;
      sessionId: string | null;
      reconnectToken: string | null;
      tickRate: number;
      snapshotRate: number;
      serverTick: number;
      /** Authoritative current control mode; sent on every welcome. */
      autopilot?: boolean;
    }
  | {
      type: "snapshot";
      tick: number;
      serverTime: number;
      lastAppliedInputSeq: number | null;
      state: RaceSnapshot;
    }
  | { type: "autopilot-state"; enabled: boolean }
  | { type: "pong"; sentAt: number; serverTime: number }
  | { type: "error"; code: string; message: string; fatal: boolean };

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);
const finite = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

/** Normalize the only identity form that may cross the snapshot boundary. */
export function normalizeAvatarLogin(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const login = value.trim().toLowerCase().replace(/^@/, "");
  return /^[a-z0-9_]{1,25}$/.test(login) ? login : undefined;
}

function hasOwn(record: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function assertExactKeys(record: Record<string, unknown>, allowed: readonly string[], path: string): void {
  const allowedSet = new Set(allowed);
  for (const key of Object.keys(record)) {
    if (!allowedSet.has(key)) throw new TypeError(`${path}.${key}: unknown field`);
  }
}

/** Validate and detach one definition delivered over the race WebSocket. */
export function normalizeCustomTrackDescriptor(value: unknown): CustomTrackDescriptor {
  if (!isRecord(value)) throw new TypeError("custom-track-definition: expected an object");
  const payload = value.type === "custom-track-definition"
    ? (() => {
      const { type: _type, ...descriptor } = value;
      return descriptor;
    })()
    : value;
  assertExactKeys(payload, ["runtimeId", "key", "revision", "def"], "custom-track-definition");
  if (!hasOwn(payload, "runtimeId") || !hasOwn(payload, "key") || !hasOwn(payload, "revision") || !hasOwn(payload, "def")) {
    throw new TypeError("custom-track-definition: missing required field");
  }
  const runtimeId = normalizeCustomTrackId(payload.runtimeId);
  const key = normalizeCustomTrackId(payload.key);
  if (!Number.isSafeInteger(payload.revision) || Number(payload.revision) <= 0) {
    throw new TypeError("custom-track-definition.revision: expected a positive safe integer");
  }
  return {
    runtimeId,
    key,
    revision: Number(payload.revision),
    def: normalizeCustomTrackDefinition(payload.def),
  };
}

/**
 * Parse a server envelope without weakening the existing snapshot contract.
 * Definition envelopes are normalized strictly; ordinary messages retain the
 * historical lightweight parsing behavior and are validated by their caller.
 */
export function parseServerMessage(raw: string): ServerMessage | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(value) || typeof value.type !== "string") return null;
  if (value.type === "custom-track-definition") return { type: value.type, ...normalizeCustomTrackDescriptor(value) };
  if (value.type === "snapshot") return normalizeSnapshotAvatarIdentities(value);
  if (value.type === "autopilot-state") {
    if (Object.keys(value).some(key => key !== "type" && key !== "enabled") || typeof value.enabled !== "boolean") return null;
    return { type: "autopilot-state", enabled: value.enabled };
  }
  if (value.type === "welcome" || value.type === "pong" || value.type === "error") return value as ServerMessage;
  return null;
}

/**
 * Keep snapshot parsing additive while normalizing the one new identity
 * field. The existing snapshot parser intentionally remains lightweight for
 * the rest of the state, but an invalid or stale avatar value must never be
 * allowed to survive in a client cache. AI slots are always identity-free.
 */
function normalizeSnapshotAvatarIdentities(value: Record<string, unknown>): ServerMessage {
  const state = value.state;
  if (!isRecord(state) || !Array.isArray(state.karts)) return value as ServerMessage;
  const karts = state.karts.map((rawKart) => {
    if (!isRecord(rawKart)) return rawKart;
    const normalized = rawKart.kind === "ai" ? undefined : normalizeAvatarLogin(rawKart.avatarLogin);
    const { avatarLogin: _avatarLogin, ...withoutAvatarLogin } = rawKart;
    return normalized ? { ...withoutAvatarLogin, avatarLogin: normalized } : withoutAvatarLogin;
  });
  return { ...value, state: { ...state, karts } } as ServerMessage;
}

export function parseClientMessage(raw: string): ClientMessage | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(value) || typeof value.type !== "string") return null;
  if (value.type === "hello") {
    if (!finite(value.protocol) || !["player", "overlay"].includes(String(value.role))) return null;
    if (value.raceClass !== undefined && value.raceClass !== "80cc" && value.raceClass !== "120cc" && value.raceClass !== "150cc") return null;
    return {
      type: "hello",
      protocol: value.protocol,
      role: value.role as ClientRole,
      name: typeof value.name === "string" ? value.name : undefined,
      color: finite(value.color) ? Math.max(0, Math.min(0xffffff, Math.floor(value.color))) : undefined,
      accentColor: finite(value.accentColor) ? Math.max(0, Math.min(0xffffff, Math.floor(value.accentColor))) : undefined,
      variant: finite(value.variant) ? Math.max(0, Math.min(6, Math.floor(value.variant))) : undefined,
      livery: finite(value.livery) ? Math.max(0, Math.min(5, Math.floor(value.livery))) : undefined,
      wheelStyle: finite(value.wheelStyle) ? Math.max(0, Math.min(4, Math.floor(value.wheelStyle))) : undefined,
      driverColor: finite(value.driverColor) ? Math.max(0, Math.min(0xffffff, Math.floor(value.driverColor))) : undefined,
      weightClass: value.weightClass === "light" || value.weightClass === "medium" || value.weightClass === "heavy" ? normalizeKartWeightClass(value.weightClass) : undefined,
      pattern: ["solid", "stripe", "rally", "split"].includes(String(value.pattern)) ? value.pattern as KartPattern : undefined,
      topper: ["none", "cap", "lime", "crown", "laurel"].includes(String(value.topper)) ? value.topper as KartTopper : undefined,
      sticker: ["none", "hop", "bolt", "pint", "medal"].includes(String(value.sticker)) ? value.sticker as KartSticker : undefined,
      underglow: finite(value.underglow) ? Math.max(0, Math.min(0xffffff, Math.floor(value.underglow))) : undefined,
      horn: ["toot", "lager", "air", "bell"].includes(String(value.horn)) ? value.horn as KartHorn : undefined,
      skidColor: finite(value.skidColor) ? Math.max(0, Math.min(0xffffff, Math.floor(value.skidColor))) : undefined,
      trail: ["bubbles", "flame", "foam", "hops", "neon", "stars", "none"].includes(String(value.trail)) ? value.trail as KartTrail : undefined,
      raceNumber: typeof value.raceNumber === "string" && /^[A-Z0-9]{0,3}$/i.test(value.raceNumber) ? value.raceNumber.toUpperCase() : undefined,
      raceClass: value.raceClass as RaceClass | undefined,
      sessionId: typeof value.sessionId === "string" ? value.sessionId : undefined,
      reconnectToken: typeof value.reconnectToken === "string" ? value.reconnectToken : undefined,
    };
  }
  if (value.type === "autopilot") {
    if (Object.keys(value).some(key => key !== "type" && key !== "enabled")) return null;
    if (typeof value.enabled !== "boolean") return null;
    return { type: "autopilot", enabled: value.enabled };
  }
  if (value.type === "input") {
    if (!Number.isSafeInteger(value.seq) || Number(value.seq) < 0 || !Number.isSafeInteger(value.targetTick) || Number(value.targetTick) < 0 || !isRecord(value.input)) return null;
    const { throttle, brake, steer, drift, useSeq } = value.input;
    if (![throttle, brake, steer].every(finite) || typeof drift !== "boolean") return null;
    if (useSeq !== undefined && (!Number.isSafeInteger(useSeq) || Number(useSeq) < 0)) return null;
    return {
      type: "input",
      seq: Number(value.seq),
      targetTick: Number(value.targetTick),
      input: {
        throttle: Math.max(0, Math.min(1, Number(throttle))),
        brake: Math.max(0, Math.min(1, Number(brake))),
        steer: Math.max(-1, Math.min(1, Number(steer))),
        drift,
        useSeq: Number(useSeq ?? 0),
      },
    };
  }
  if (value.type === "leave") return { type: "leave" };
  if (value.type === "ping" && finite(value.sentAt)) return { type: "ping", sentAt: value.sentAt };
  return null;
}

export function encodeServerMessage(message: ServerMessage): string {
  return JSON.stringify(message);
}

export function toRaceSnapshot(state: RaceState): RaceSnapshot {
  return {
    trackId: state.trackId,
    raceVariant: { ...state.raceVariant },
    raceClass: state.raceClass,
    phase: state.phase,
    elapsed: state.elapsed,
    countdown: state.countdown,
    totalLaps: state.totalLaps,
    trackLength: state.trackLength,
    karts: state.karts.map((kart) => {
      const avatarLogin = kart.controller === "twitch"
        ? normalizeAvatarLogin(kart.twitchLogin)
        : kart.controller === "human" ? normalizeAvatarLogin(kart.avatarLogin) : undefined;
      return {
      id: kart.id,
      ...(kart.driverProfileId ? { driverProfileId: kart.driverProfileId } : {}),
      ...(avatarLogin ? { avatarLogin } : {}),
      name: kart.name,
      color: kart.color,
      accentColor: kart.accentColor,
      variant: kart.variant,
      livery: kart.livery,
      wheelStyle: kart.wheelStyle,
      driverColor: kart.driverColor,
      weightClass: kart.weightClass,
      pattern: kart.pattern,
      topper: kart.topper,
      sticker: kart.sticker,
      underglow: kart.underglow,
      horn: kart.horn,
      skidColor: kart.skidColor,
      trail: kart.trail,
      raceNumber: kart.raceNumber,
       kind: kart.controller,
       autopilot: kart.controller === "human" && kart.autopilot === true,
       x: kart.x,
      y: kart.y,
      z: kart.z,
      velocityX: kart.velocityX,
      velocityZ: kart.velocityZ,
      yaw: kart.yaw,
      speed: kart.speed,
      launchCharge: kart.launchCharge,
      distance: kart.distance,
      lateral: kart.lateral,
      driftCharge: kart.driftCharge,
      driftDirection: kart.driftDirection,
      boostTime: kart.boostTime,
      itemDodgeTime: kart.itemDodgeTime,
      itemImmunityTime: kart.itemImmunityTime,
      ghostTime: kart.ghostTime,
      hiccupTime: kart.hiccupTime,
      draftCharge: kart.draftCharge,
      draftBoostTime: kart.draftBoostTime,
      draftCooldown: kart.draftCooldown,
      draftTargetId: kart.draftTargetId,
      draftBoostCount: kart.draftBoostCount,
      heldItem: kart.heldItem,
      itemHitCount: kart.itemHitCount,
      lastItemHit: kart.lastItemHit,
      itemStealCount: kart.itemStealCount,
      lastItemStolen: kart.lastItemStolen,
      itemGuardCount: kart.itemGuardCount,
      kegDodgeCount: kart.kegDodgeCount,
      lastItemDodge: kart.lastItemDodge,
      shieldTime: kart.shieldTime,
      spinTime: kart.spinTime,
      spinRecoveryTime: kart.spinRecoveryTime,
      hornSeq: kart.hornSeq,
      verticalVelocity: kart.verticalVelocity,
      airborne: kart.airborne,
      airTime: kart.airTime,
      trickQueued: kart.trickQueued,
      classicJumpedSampleIndex: kart.classicJumpedSampleIndex,
      classicSampleIndex: kart.classicSampleIndex,
      classicNextCheckpoint: kart.classicNextCheckpoint,
      classicGatesPassed: kart.classicGatesPassed,
      classicProgress: kart.classicProgress,
      onShortcut: kart.onShortcut,
      surface: kart.surface,
      offRoadTime: kart.offRoadTime,
      wrongWayTime: kart.wrongWayTime,
      recoveryTime: kart.recoveryTime,
      recoveryCount: kart.recoveryCount,
      courseEventCooldown: kart.courseEventCooldown,
      courseEventHitCount: kart.courseEventHitCount,
      signatureZoneId: kart.signatureZoneId,
      signatureZoneTime: kart.signatureZoneTime,
      signatureZoneTriggerCount: kart.signatureZoneTriggerCount,
      signatureZoneLastLap: kart.signatureZoneLastLap,
      safeDistance: kart.safeDistance,
      lap: kart.lap,
      place: kart.place,
      finishedAt: kart.finishedAt,
      };
    }),
    itemBoxes: state.itemBoxes.map((box) => ({ ...box })),
    projectiles: state.projectiles.map((projectile) => ({ ...projectile })),
    hazards: state.hazards.map((hazard) => ({ ...hazard })),
    courseEvent: state.courseEvent ? { ...state.courseEvent } : null,
    cup: state.cup ? cloneCup(state.cup) : undefined,
  };
}
